/* This page is for RFX Evaluation Approval and Display page */

import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";

//Redux
import { useSelector } from "react-redux";

//Serivce
import ItemService from "../../../../services/supplier_appCommonService";

//Components
import Layout from "../../../../Layout/Layout";
import { InputField } from "../../../../Components/formElements/InputField";
import ShowHorizontalStatus from "../../../../Components/Modal/supplier_app/Workflow/ShowHorizontalStatus";
import WorkflowApproveReject from "../../../../Components/Workflow/WorkflowApproveReject";
import { formatDateTime } from "../../../../common/Validations";
import Loader from "../../../../Components/Loader";
import { ComboboxField } from "../../../../Components/formElements/ComboboxField";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";
import FileViewerModal from "../../../../Components/FileViewer/FileViewerModal";

// ComboboxField's getvalue default is a fresh arrow each render, so passing an
// inline (or omitting) it makes its effect loop. A module-level constant keeps
// the reference stable. Defined here and passed as a NOOP on the read-only
// ComboboxFields in RenderInputField below.
const NOOP = () => {};

// Questionnaire answer field rendered per question (READ-ONLY display).
// Declared at MODULE level so its type reference is stable (a component defined
// inside the parent is a NEW type each render, which makes React remount every
// input and reset dropdowns/lose focus). Values come from the submitted payload
// stored on each question object as `questionData.answer`.
// Criteria / weightage / attachment also come off the same question object
// (`criteria`, `weightage`, `attachment`) and are shown read-only. Older RFQs
// (or questions saved before these fields existed) simply omit them, so each is
// rendered only when its data is present.
const RenderInputField = ({ questionData, onViewAttachment }) => {
  const {
    componentId,
    mandatory,
    question,
    answerDefinition,
    serialNo,
    criteria,
    weightage,
  } = questionData;
  const answerValue = questionData?.answer;
  const attachmentValue = questionData?.attachment;

  // The question's answer input (read-only). Kept as a helper so the criteria/
  // weightage and attachment blocks can be appended after it in the shared Row.
  const renderAnswer = () => {
    switch (answerDefinition?.type) {
      case "textarea":
        return (
          <Col xs={12} md={6} lg={6} className="commLeftRightPadding">
            <TextAreaField
              label={serialNo + ". " + question?.label}
              id={componentId}
              name={componentId}
              className="inputBox"
              placeholder=""
              value={answerValue || ""}
              disabled
              required={mandatory}
            />
          </Col>
        );
      case "select": {
        // Match the stored answer back to its option so the ComboboxField can
        // display the selected label (it renders value via the option object).
        const selectedOption =
          (answerDefinition?.options || []).find(
            (o) =>
              o?.name === answerValue ||
              o?.value === answerValue ||
              String(o?.id) === String(answerValue),
          ) || null;
        return (
          <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding">
            <ComboboxField
              label={serialNo + ". " + question?.label}
              placeholder=""
              data={answerDefinition?.options}
              id={componentId}
              iconClassName="dropdownIcon"
              name={componentId}
              code={componentId}
              description="name"
              setValue={selectedOption}
              disabled
              required={mandatory}
              // getvalue must be a STABLE reference: ComboboxField's getvalue
              // default is a fresh arrow each render, so omitting it (or passing
              // an inline one) makes its effect loop. Pass the stable NOOP.
              getvalue={NOOP}
            />
          </Col>
        );
      }
      case "radio":
        return (
          <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding">
            <label className="block text-sm font-medium leading-6 text-gray-900 infoIconAccordion w-100">
              {serialNo + ". " + question?.label} {mandatory && "*"}
            </label>

            {answerDefinition?.options.map((option, index) => {
              const optVal = option?.value ?? option?.name ?? "";
              return (
                <div key={index} className="block">
                  <input
                    type="radio"
                    className="form-check-input fs-5 ml-3"
                    id={`${componentId}-${index}`}
                    name={componentId}
                    value={optVal}
                    checked={answerValue === optVal}
                    disabled
                    required={mandatory}
                  />
                  <span className="ml-3">{option?.value} </span>
                </div>
              );
            })}
          </Col>
        );
      case "checkbox":
        return (
          <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding">
            <label className="block text-sm font-medium leading-6 text-gray-900 infoIconAccordion w-100">
              {serialNo + ". " + question?.label} {mandatory && "*"}
            </label>

            {answerDefinition?.options.map((option, index) => {
              const optVal = option?.value ?? option?.name ?? "";
              const isChecked =
                Array.isArray(answerValue) && answerValue.includes(optVal);
              return (
                <div key={index} className="block">
                  <input
                    type="checkbox"
                    className="form-check-input fs-5 ml-3"
                    id={`${componentId}-${index}`}
                    name={componentId}
                    value={optVal}
                    checked={isChecked}
                    disabled
                    required={mandatory}
                  />
                  <span className="ml-3">{option?.value} </span>
                </div>
              );
            })}
          </Col>
        );
      case "text":
        return (
          <Col
            xs={12}
            md={6}
            lg={6}
            className="commTopButtonRightLeftPadding block"
          >
            <InputField
              label={serialNo + ". " + question?.label}
              type="text"
              className="inputBox"
              id={componentId}
              name={componentId}
              placeholder=""
              value={answerValue || ""}
              disabled
              required={mandatory}
            />
          </Col>
        );
      case "number":
        return (
          <Col
            xs={12}
            md={6}
            lg={6}
            className="commTopButtonRightLeftPadding block"
          >
            <InputField
              label={serialNo + ". " + question?.label}
              type="number"
              className="inputBox"
              id={componentId}
              name={componentId}
              placeholder=""
              value={answerValue || ""}
              disabled
              required={mandatory}
            />
          </Col>
        );
      default:
        return null;
    }
  };

  // Read-only Criteria + Weightage. Always shown so the Commercial and Technical
  // questionnaire blocks render identically. Questions built with the form builder
  // carry `criteria`/`weightage`; questions from older RFQs (or templates saved
  // before these fields existed) omit them, so we fall back to the SAME defaults
  // the builder uses — "Weighted" criteria and 0 weightage — instead of hiding
  // the fields.
  const hasCriteria =
    criteria !== undefined && criteria !== null && criteria !== "";
  const hasWeightage =
    weightage !== undefined && weightage !== null && weightage !== "";
  const renderCriteriaWeightage = () => {
    return (
      <Col xs={12} md={6} lg={6} className="commLeftRightPadding">
        <Row>
          <Col xs={12} md={6} lg={6} className="commLeftRightPadding">
            <InputField
              label="Criteria"
              className="inputBox"
              value={hasCriteria ? criteria : "Weighted"}
              disabled
            />
          </Col>
          <Col xs={12} md={6} lg={6} className="commLeftRightPadding">
            <InputField
              label="Weightage"
              type="number"
              className="inputBox"
              value={hasWeightage ? weightage : 0}
              disabled
            />
          </Col>
        </Row>
      </Col>
    );
  };

  // Read-only attachment viewer. Rendered only when an attachment id exists.
  const renderAttachment = () => {
    if (!attachmentValue) return null;
    return (
      <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
        <label className="block text-sm font-medium leading-6 text-gray-900 infoIconAccordion w-100">
          Attachment
        </label>
        <img
          src="assets/img/newIcons/attached-file.png"
          className="mt-1 attachmentIcon"
          alt="attachments"
          style={{ cursor: "pointer" }}
          onClick={() => onViewAttachment && onViewAttachment(attachmentValue)}
        />
      </Col>
    );
  };

  const answerField = renderAnswer();
  // Unknown answer type → render nothing (and no orphan criteria/attachment).
  if (!answerField) return null;

  return (
    <React.Fragment>
      {answerField}
      {renderCriteriaWeightage()}
      {renderAttachment()}
    </React.Fragment>
  );
};

// Ordered wizard steps for the RFX Evaluation (workflow) page. The header
// tabs, tab panels and the Previous / Next buttons all key off this single
// list so the order stays in sync. The WorkflowApproveReject bar renders
// above the card (always visible, outside the tabs).
const tabList = [
  { id: "rfq-title", label: "RFQ Title" },
  { id: "configuration", label: "Configuration" },
  { id: "schedule", label: "Schedule" },
  { id: "line-items", label: "Line Items" },
  { id: "suppliers", label: "Suppliers" },
  { id: "quote-summary", label: "Quote Summary" },
  { id: "tech-comm", label: "Tech & Comm Template" },
];

const RfxEvaluationDetails = () => {
  localStorage.setItem("menu", "Sourcing");
  localStorage.setItem("submenu", "Request for Quotation");

  let rfxNr = useSelector((state) => state?.sourcing?.rfq?.rfxNr);
  let workflowConfig = useSelector((state) => state?.common?.data?.workflow);
  let workflow = useSelector((state) => state?.sourcing?.rfq?.workflowRFX);

  const [formInputs, setFormInputs] = useState({
    rfxType: "",
    productType: "",
    rfxValue: "",
    rfxHeaderStatus: "",
    rfxbidType: "",
    rfxCurrency: "",
    rfxTitle: "",
    rfxDescription: "",
    openingDateTime: "",
    closingDateTime: "",
    totalHour: "",
    rfxCategoryDesc: "",
    typeOfPurchase: "",
    headerIncoTerms: "",
    incoTermsLocation: "",
    procurementStrategies: "",
    items: [],
    formCOMM: "",
    formTECH: "",
    termsConditions: [],
    supplierList: [],
    othersCharge: [],
    // Additional fields from API response
    rfxNoOfEvaluationType: "",
    rfxTechFocalPersonId: "",
    technicalApprovalType: "",
    initiator: "",
    plant: "",
    companyAddress: "",
    rfxVersion: "",
    buyerEmail: "",
    billingAddress: "",
    createdOn: "",
    buyerConact: "",
    deliveryAddressCodeDesc: "",
    rfxCategory: "",
    othercurrencies: "",
    paymentTerms: "",
    INCOTerms: "",
    reminderEnabled: "",
    instructionsAttachment: "",
    rfxInstruction: "",
    scopeOfwork: "",
    drawing: "",
    annexures: "",
    others: "",
  });
  console.log(">>formInputs", formInputs);

  // Toggle states for all accordion sections
  const [isCardVisible1, setCardVisible1] = useState(true);
  const [isCardVisible2, setCardVisible2] = useState(true);
  const [isCardVisible3, setCardVisible3] = useState(true);
  const [isCardVisible4, setCardVisible4] = useState(true);
  const [isCardVisible5, setCardVisible5] = useState(true);
  const [isCardVisible6, setCardVisible6] = useState(true);
  const [isCardVisible7, setCardVisible7] = useState(true);
  const [isCardVisible8, setCardVisible8] = useState(true);
  const [isCardVisible9, setCardVisible9] = useState(true);
  const [isCardVisible10, setCardVisible10] = useState(true);
  const [isCardVisible11, setCardVisible11] = useState(true);
  const [isCardVisible12, setCardVisible12] = useState(true);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("rfq-title");

  const toggleCardVisibility1 = () => setCardVisible1(!isCardVisible1);
  const toggleCardVisibility2 = () => setCardVisible2(!isCardVisible2);
  const toggleCardVisibility3 = () => setCardVisible3(!isCardVisible3);
  const toggleCardVisibility4 = () => setCardVisible4(!isCardVisible4);
  const toggleCardVisibility5 = () => setCardVisible5(!isCardVisible5);
  const toggleCardVisibility6 = () => setCardVisible6(!isCardVisible6);
  const toggleCardVisibility7 = () => setCardVisible7(!isCardVisible7);
  const toggleCardVisibility8 = () => setCardVisible8(!isCardVisible8);
  const toggleCardVisibility9 = () => setCardVisible9(!isCardVisible9);
  const toggleCardVisibility10 = () => setCardVisible10(!isCardVisible10);
  const toggleCardVisibility11 = () => setCardVisible11(!isCardVisible11);
  const toggleCardVisibility12 = () => setCardVisible12(!isCardVisible12);

  const loadInitialPageData = async () => {
    if (rfxNr) {
      let obj = {
        documentNumber: rfxNr,
      };
      const toastId = toast.loading("Loading...", {
        position: "top-right",
      });
      await ItemService.getRfxDocumentDetail(obj).then((item) => {
        if (item?.status === 200) {
          console.log(">>item", item);
          setFormInputs(item.data);
        } else {
          toast.error(item?.message || "Please try again !!", {
            duration: 4000,
            position: "top-right",
          });
        }
        toast.dismiss(toastId);
      });
      setLoading(false);
    } else {
      window.history.back();
    }
  };

  useEffect(() => {
    loadInitialPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfxNr]);

  const handleViewFile = (filePath) => {
    if (filePath) {
      window.open(filePath, "_blank");
    }
  };

  // Questionnaire attachments are stored as an attachmentId (not a direct URL),
  // so they are viewed through the FileViewerModal (as in BuyerRFQDetails.js)
  // rather than window.open used by handleViewFile above.
  const [getShowFileViewModal, setShowFileViewModal] = useState(false);
  const [getFileViewData, setFileViewData] = useState(null);
  const handleViewAttachment = (attachmentId) => {
    setFileViewData(attachmentId);
    setShowFileViewModal(true);
  };
  const handleViewFileHide = () => {
    setShowFileViewModal(false);
  };

  // Sum of each questionnaire block's weightage, shown in the block header the
  // same way BuyerRFQDetails.js / CreateRFQTabs.js do. Questions without a
  // weightage contribute 0, and a non-array formCOMM/formTECH is tolerated.
  const commercialWeightSum = (
    Array.isArray(formInputs.formCOMM) ? formInputs.formCOMM : []
  ).reduce((sum, q) => sum + (Number(q?.weightage) || 0), 0);
  const technicalWeightSum = (
    Array.isArray(formInputs.formTECH) ? formInputs.formTECH : []
  ).reduce((sum, q) => sum + (Number(q?.weightage) || 0), 0);

  // Column definitions for line items (read-only)
  const lineItemsColumns = [
    {
      name: "Document Item Nr",
      selector: (row) => row.local_id || row.itemNumber || "--",
      sortable: true,
      width: "160px",
    },
    {
      name: "Material/Service",
      selector: (row) => row.materialCode || "--",
      sortable: true,
      width: "180px",
    },
    {
      name: "Description",
      selector: (row) => row.materialDescription || "--",
      sortable: true,
      width: "200px",
    },
    {
      name: "Quantity",
      selector: (row) => row.purchaseQuantity || "--",
      sortable: true,
      width: "120px",
    },
    {
      name: "UOM",
      selector: (row) => row.orderUnitOfMeasure || "--",
      sortable: true,
      width: "100px",
    },
    {
      name: "Unit Price",
      selector: (row) => row.orderPriceUnit || "--",
      sortable: true,
      width: "130px",
    },
    {
      name: "Item Value",
      selector: (row) => row.netValue || "--",
      sortable: true,
      width: "130px",
    },
    {
      name: "Currency",
      selector: (row) => row.currencyCode || "INR",
      width: "100px",
    },
    {
      name: "Delivery Date",
      selector: (row) => row.deliveryDate || "--",
      width: "140px",
    },
    {
      name: "Plant",
      selector: (row) => row.plant || "--",
      sortable: true,
      width: "140px",
    },
  ];

  // Column definitions for suppliers (read-only)
  const suppliersColumns = [
    {
      name: "Supplier ID",
      selector: (row) => row.AssociateNumber || row.associateNumber || "--",
      sortable: true,
      width: "120px",
    },
    {
      name: "Supplier Name",
      selector: (row) => row.organisationName || "--",
      sortable: true,
      width: "200px",
    },
    {
      name: "Contact Person",
      selector: (row) => row.contactfirstName || row.contactPerson || "--",
      sortable: true,
      width: "160px",
    },
    {
      name: "Contact Number",
      selector: (row) => row.contactNumber || "--",
      sortable: true,
      width: "150px",
    },
    {
      name: "Email",
      selector: (row) => row.email || "--",
      sortable: true,
      width: "220px",
    },
    {
      name: "ICV Score %",
      selector: (row) => row.totalScore || "--",
      sortable: true,
      width: "130px",
    },
  ];

  // Wizard tab navigation helpers. Current index is derived from the active
  // tab so Previous / Next can be shown conditionally below the tabs.
  const currentTabIndex = tabList.findIndex((tab) => tab.id === activeTab);
  const isLastTab = currentTabIndex === tabList.length - 1;
  const goToPreviousTab = () => {
    if (currentTabIndex > 0) setActiveTab(tabList[currentTabIndex - 1].id);
  };
  const goToNextTab = () => {
    if (currentTabIndex < tabList.length - 1)
      setActiveTab(tabList[currentTabIndex + 1].id);
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <Loader isLoading={loading} className={"text-center"} />
        </Col>
        {!loading && (
          <Col xs={12} md={12} className="rightSide">
            <div className="rightSideInner PageCommonCSS">
              <h2 className="mainTitle">
                RFX Evaluation for
                <span className="bg-table-status bg-table-documentNumber-status headerStatusDetailedPage">
                  {formInputs?.rfxNr}
                </span>
                <span>
                  {formInputs?.rfxHeaderStatus === "Published" ? (
                    <span className="bg-table-status bg-table-success-status headerStatusDetailedPage">
                      {formInputs?.rfxHeaderStatus}
                    </span>
                  ) : formInputs?.rfxHeaderStatus === "Cancelled" ? (
                    <span className="bg-table-status bg-table-danger-status headerStatusDetailedPage">
                      {formInputs?.rfxHeaderStatus}
                    </span>
                  ) : (
                    formInputs?.rfxHeaderStatus
                  )}
                </span>
              </h2>
              <p className="subText">RFX Evaluation Details</p>
              <hr />

              {/* Workflow Component */}
              <WorkflowApproveReject
                workflowData={workflow}
                formInputsData={formInputs}
              />

              {workflowConfig?.showInDetailPage &&
                formInputs?.workflowActionLogs?.length > 0 && (
                  <ShowHorizontalStatus
                    rowdatamodal={formInputs?.workflowActionLogs}
                  />
                )}

              <div className="card">
                <div className="card-body">
                  <ul className="nav nav-tabs" id="myTab" role="tablist">
                    {tabList.map((tab) => {
                      const isActive = activeTab === tab.id;
                      return (
                        <li
                          className="nav-item"
                          role="presentation"
                          key={tab.id}
                        >
                          <button
                            className={`nav-link ${isActive ? "active" : ""}`}
                            id={`${tab.id}-tab`}
                            type="button"
                            role="tab"
                            aria-controls={tab.id}
                            aria-selected={isActive ? "true" : "false"}
                            onClick={() => setActiveTab(tab.id)}
                          >
                            {tab.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="tab-content pt-2" id="myTabContent">
                    <div
                      className={`tab-pane fade ${
                        activeTab === "rfq-title" ? "show active" : ""
                      }`}
                      id="rfq-title"
                      role="tabpanel"
                      aria-labelledby="rfq-title-tab"
                    >
              {/* 01 - Basic Information */}
              <div className="accordion mb-4" id="rfx-accordion-01">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility1}
                          className={`bi ${
                            isCardVisible1
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">RT</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">RFQ Type</span>
                        <span className="subHeaderTitle">
                          Basic Information
                        </span>
                      </Col>
                    </Row>
                  </h2>

                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible1 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <ComboboxField
                            label="RFQ Type"
                            placeholder=""
                            // data={getRFxType}
                            id="rfxType"
                            className="inputBox"
                            iconClassName="dropdownIcon"
                            name="rfxType"
                            code="rfxTypeCode"
                            description="rfxTypeDesc"
                            setValue={formInputs.rfxTypeDesc || ""}
                            // getvalue={setDropdownData}
                            // className="dropdownOption"
                            disabled
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <ComboboxField
                            label="Product Type"
                            className="inputBox"
                            placeholder=""
                            // data={getRFxProductType}
                            id="productType"
                            iconClassName="dropdownIcon"
                            name="productType"
                            code="productTypeCode"
                            description="productTypeDesc"
                            setValue={formInputs.productType || ""}
                            // getvalue={setDropdownData}
                            // className="dropdownOption"
                            disabled
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Estimated RFQ Value *"
                            id="rfxValue"
                            name="rfxValue"
                            placeholder=""
                            value={formInputs.rfxValue || ""}
                            disabled
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <ComboboxField
                            label="Bid Type"
                            className="inputBox"
                            placeholder=""
                            // data={getRfxBidType}
                            id="rfxbidType"
                            iconClassName="dropdownIcon"
                            name="rfxbidType"
                            code="rfxbidTypeCode"
                            description="rfxbidTypeDesc"
                            setValue={formInputs.rfxbidTypeDesc || ""}
                            // getvalue={setDropdownData}
                            // className="dropdownOption"
                            disabled
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <ComboboxField
                            label="Number Of Evaluation"
                            placeholder=""
                            className="inputBox"
                            // data={getRfxNoOfEvaluationType}
                            id="rfxNoOfEvaluationType"
                            iconClassName="dropdownIcon"
                            name="rfxNoOfEvaluationType"
                            code="rfxNoOfEvaluationTypeCode"
                            description="rfxNoOfEvaluationTypeDesc"
                            setValue={
                              formInputs.rfxNoOfEvaluationTypeDesc || ""
                            }
                            // getvalue={setDropdownData}
                            // className="dropdownOption"
                            disabled
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <ComboboxField
                            label="Currency"
                            className="inputBox"
                            placeholder=""
                            // data={getcurrency}
                            id="rfxCurrency"
                            iconClassName="dropdownIcon"
                            name="rfxCurrency"
                            code="rfxCurrencyCode"
                            description="rfxCurrencyDesc"
                            setValue={formInputs.rfxCurrency || ""}
                            // getvalue={setDropdownData}
                            // className="dropdownOption"
                            disabled
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <ComboboxField
                            className="inputBox"
                            label="Tech Focal Point *"
                            placeholder=""
                            // data={getTechnicalUserList}
                            id="rfxTechFocalPersonId"
                            iconClassName="dropdownIcon"
                            name="rfxTechFocalPersonId"
                            code="rfxTechFocalPersonIdCode"
                            description="rfxTechFocalPersonIdDesc"
                            setValue={formInputs.rfxTechFocalPersonId || ""}
                            // getvalue={setDropdownData}
                            // className="dropdownOption"
                            disabled
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <ComboboxField
                            className="inputBox"
                            label="Technical Approval Type"
                            placeholder=""
                            // data={getTechnicalApprovalType}
                            id="technicalApprovalType"
                            iconClassName="dropdownIcon"
                            name="technicalApprovalType"
                            code="technicalApprovalTypeCode"
                            description="technicalApprovalTypeDesc"
                            setValue={
                              formInputs.technicalApprovalTypeDesc || ""
                            }
                            // getvalue={setDropdownData}
                            // className="dropdownOption"
                            disabled
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>
              {/* 02 - Header Description */}
              <div className="accordion mb-4" id="rfx-accordion-02">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility2}
                          className={`bi ${
                            isCardVisible2
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">RH</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">RFQ Header</span>
                        <span className="subHeaderTitle">
                          Header Description
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible2 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Buyer Responsible"
                            name="initiator"
                            value={formInputs?.initiator || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Create / Initiator"
                            name="initiator"
                            value={
                              formInputs?.initiatorName
                                ? `${formInputs?.initiator} - ${formInputs?.initiatorName}`
                                : formInputs?.initiator || ""
                            }
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Plant"
                            name="plant"
                            value={formInputs?.plant || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Company Address"
                            name="companyAddress"
                            value={formInputs?.companyAddress || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="RFQ Version"
                            name="rfxVersion"
                            value={formInputs?.rfxVersion || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="RFQ Creation"
                            name="rfqCreation"
                            value={formInputs?.rfqCreation || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Buyer Email"
                            name="buyerEmail"
                            value={formInputs?.buyerEmail || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Billing Address"
                            name="billingAddress"
                            value={formInputs?.billingAddress || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Created On"
                            name="createdOn"
                            value={formInputs?.createdOn || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Buyer Contact"
                            name="buyerConact"
                            value={formInputs?.buyerConact || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Delivery Address"
                            name="deliveryAddressCodeDesc"
                            value={formInputs?.deliveryAddressCodeDesc || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="RFQ Category"
                            name="rfxCategory"
                            value={
                              formInputs?.rfxCategory ||
                              formInputs?.rfxCategoryDesc ||
                              ""
                            }
                            disabled={true}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

                    </div>
                    <div
                      className={`tab-pane fade ${
                        activeTab === "configuration" ? "show active" : ""
                      }`}
                      id="configuration"
                      role="tabpanel"
                      aria-labelledby="configuration-tab"
                    >
              {/* 03 - Header Information */}
              <div className="accordion mb-4" id="rfx-accordion-03">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility3}
                          className={`bi ${
                            isCardVisible3
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">HI</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">Header Info</span>
                        <span className="subHeaderTitle">
                          Header Information
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible3 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Title"
                            name="rfxTitle"
                            value={formInputs?.rfxTitle || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Description"
                            name="rfxDescription"
                            value={formInputs?.rfxDescription || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Type of Purchase"
                            name="typeOfPurchase"
                            value={formInputs?.typeOfPurchase || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Other Currencies"
                            name="othercurrencies"
                            value={
                              Array.isArray(formInputs?.otherCurrencies)
                                ? formInputs?.otherCurrencies.join(", ")
                                : formInputs?.otherCurrencies || ""
                            }
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Payment Term"
                            name="paymentTerms"
                            value={formInputs?.paymentTerms || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="INCO Terms"
                            name="INCOTerms"
                            value={
                              Array.isArray(formInputs?.incoTerms)
                                ? formInputs?.incoTerms
                                    .map((t) => t.name || t.description)
                                    .join(", ")
                                : formInputs?.incoTerms?.name ||
                                  formInputs?.incoTerms?.description ||
                                  ""
                            }
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="INCO Terms Location"
                            name="incoTermsLocation"
                            value={formInputs?.incoTermsLocation || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Reminder Enabled"
                            name="reminderEnabled"
                            value={formInputs?.reminderEnabled || ""}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Instructions Attachment"
                            name="instructionsAttachment"
                            value={
                              formInputs?.instructionsAttachment
                                ? "Attached"
                                : "--"
                            }
                            disabled={true}
                          />
                          {formInputs?.instructionsAttachment && (
                            <img
                              src="assets/img/newIcons/attached-file.png"
                              className="mt-1 attachmentIcon"
                              alt="attachments"
                              onClick={() =>
                                handleViewFile(
                                  formInputs?.instructionsAttachment,
                                )
                              }
                              style={{ cursor: "pointer" }}
                            />
                          )}
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

              {/* 04 - Instructions Section */}
              <div className="accordion mb-4" id="rfx-accordion-04">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility4}
                          className={`bi ${
                            isCardVisible4
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">RE</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">Instructions</span>
                        <span className="subHeaderTitle">
                          Instructions Section
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible4 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row className="p-4">
                        <Col xs={12} md={12} lg={12}>
                          {formInputs?.instructions ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: formInputs?.instructions,
                              }}
                            />
                          ) : (
                            <p className="text-muted">
                              No instructions provided.
                            </p>
                          )}
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

              {/* 05 - Documents Form */}
              <div className="accordion mb-4" id="rfx-accordion-05">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility5}
                          className={`bi ${
                            isCardVisible5
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">TD</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">Documents</span>
                        <span className="subHeaderTitle">Documents Form</span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible5 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row>
                        {/* Terms & Conditions */}
                        <h5 className="card-title">Terms & Conditions</h5>
                        <Row>
                          <Col xs={12} md={12} lg={8}>
                            {formInputs?.termsConditions?.length > 0 ? (
                              formInputs?.termsConditions.map((item, i) => (
                                <div className="form-check" key={item.id || i}>
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`gridCheck${item.id || i}`}
                                    checked={
                                      item.status === true ||
                                      item.status === "true"
                                    }
                                    disabled={true}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`gridCheck${item.id || i}`}
                                  >
                                    {item.name}
                                  </label>
                                </div>
                              ))
                            ) : (
                              <p className="text-muted">
                                No terms & conditions available.
                              </p>
                            )}
                          </Col>
                        </Row>

                        {/* Documents */}
                        <h1 className="card-title mt-3">Documents :</h1>
                        <Row>
                          <Col
                            xs={12}
                            md={4}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="1.1. SCOPE OF WORK"
                              name="scopeOfwork"
                              value={
                                formInputs?.scopeOfwork
                                  ? "Attached"
                                  : "Not Attached"
                              }
                              disabled={true}
                            />
                            {formInputs?.scopeOfwork && (
                              <img
                                src="assets/img/newIcons/attached-file.png"
                                className="mt-1 attachmentIcon"
                                alt="attachments"
                                onClick={() =>
                                  handleViewFile(formInputs?.scopeOfwork)
                                }
                                style={{ cursor: "pointer" }}
                              />
                            )}
                          </Col>
                          <Col
                            xs={12}
                            md={4}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="1.2. DRAWING"
                              name="drawing"
                              value={
                                formInputs?.drawing
                                  ? "Attached"
                                  : "Not Attached"
                              }
                              disabled={true}
                            />
                            {formInputs?.drawing && (
                              <img
                                src="assets/img/newIcons/attached-file.png"
                                className="mt-1 attachmentIcon"
                                alt="attachments"
                                onClick={() =>
                                  handleViewFile(formInputs?.drawing)
                                }
                                style={{ cursor: "pointer" }}
                              />
                            )}
                          </Col>
                          <Col
                            xs={12}
                            md={4}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="1.3. ANNEXURES"
                              name="annexures"
                              value={
                                formInputs?.annexures
                                  ? "Attached"
                                  : "Not Attached"
                              }
                              disabled={true}
                            />
                            {formInputs?.annexures && (
                              <img
                                src="assets/img/newIcons/attached-file.png"
                                className="mt-1 attachmentIcon"
                                alt="attachments"
                                onClick={() =>
                                  handleViewFile(formInputs?.annexures)
                                }
                                style={{ cursor: "pointer" }}
                              />
                            )}
                          </Col>
                          <Col
                            xs={12}
                            md={4}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="1.4. OTHERS"
                              name="others"
                              value={
                                formInputs?.others ? "Attached" : "Not Attached"
                              }
                              disabled={true}
                            />
                            {formInputs?.others && (
                              <img
                                src="assets/img/newIcons/attached-file.png"
                                className="mt-1 attachmentIcon"
                                alt="attachments"
                                onClick={() =>
                                  handleViewFile(formInputs?.others)
                                }
                                style={{ cursor: "pointer" }}
                              />
                            )}
                          </Col>
                        </Row>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

              {/* 06 - Charges Selection */}
              <div className="accordion mb-4" id="rfx-accordion-06">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility6}
                          className={`bi ${
                            isCardVisible6
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">CS</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">Charges Selection</span>
                        <span className="subHeaderTitle">
                          Charges Selection
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible6 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row className="commTopButtonRightLeftPadding">
                        <h5 className="card-title">Select Charges</h5>
                        <Row>
                          <Col xs={12} md={12} lg={8}>
                            {formInputs?.othersCharge?.length > 0 ? (
                              formInputs?.othersCharge.map((charge, idx) => (
                                <div
                                  className="form-check"
                                  key={charge.id || idx}
                                >
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`charge-${charge.id || idx}`}
                                    checked={true}
                                    disabled={true}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`charge-${charge.id || idx}`}
                                  >
                                    {charge.name ||
                                      charge.chargeName ||
                                      charge.description ||
                                      ""}
                                  </label>
                                </div>
                              ))
                            ) : (
                              <p className="text-muted">No charges selected.</p>
                            )}
                          </Col>
                        </Row>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

                    </div>
                    <div
                      className={`tab-pane fade ${
                        activeTab === "line-items" ? "show active" : ""
                      }`}
                      id="line-items"
                      role="tabpanel"
                      aria-labelledby="line-items-tab"
                    >
              {/* 07 - Material/Service Details */}
              <div className="accordion mb-4" id="rfx-accordion-07">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility7}
                          className={`bi ${
                            isCardVisible7
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">LI</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">Line Items</span>
                        <span className="subHeaderTitle">
                          Material/ Service Details
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible7 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row>
                        <Col xs={12} md={12} lg={12}>
                          <DataTable
                            columns={lineItemsColumns}
                            data={
                              Array.isArray(formInputs?.items)
                                ? formInputs?.items
                                : []
                            }
                            pagination
                            paginationRowsPerPageOptions={[5, 10, 25, 50]}
                            paginationPerPage={10}
                            noDataComponent={
                              <p className="my-4">No line items available.</p>
                            }
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

                    </div>
                    <div
                      className={`tab-pane fade ${
                        activeTab === "suppliers" ? "show active" : ""
                      }`}
                      id="suppliers"
                      role="tabpanel"
                      aria-labelledby="suppliers-tab"
                    >
              {/* 08 - Suppliers */}
              <div className="accordion mb-4" id="rfx-accordion-08">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility8}
                          className={`bi ${
                            isCardVisible8
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">SP</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">
                          Suppliers ({formInputs?.supplierList?.length || 0})
                        </span>
                        <span className="subHeaderTitle">Suppliers List</span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible8 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row>
                        <Col xs={12} md={12} lg={12}>
                          <DataTable
                            columns={suppliersColumns}
                            data={formInputs?.supplierList || []}
                            pagination
                            paginationRowsPerPageOptions={[5, 10, 25, 50]}
                            paginationPerPage={10}
                            noDataComponent={
                              <p className="my-4">No suppliers available.</p>
                            }
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

              {/* 09 - Supplier Status */}
              {formInputs?.rfxSupplierHeaderStatus && (
                <div className="accordion mb-4" id="rfx-accordion-09">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row
                        style={{ padding: "6px 14px", alignItems: "center" }}
                      >
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-left arrowBtn"
                        >
                          <i
                            onClick={toggleCardVisibility9}
                            className={`bi ${
                              isCardVisible9
                                ? "bi-chevron-down"
                                : "bi-chevron-right"
                            }`}
                          ></i>
                        </Col>
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-center logoBtn"
                        >
                          <span className="iconLogo">SS</span>
                        </Col>
                        <Col xs={8} md={9} lg={9}>
                          <span className="headerTitle">Supplier Status</span>
                          <span className="subHeaderTitle">
                            Supplier RFQ Status
                          </span>
                        </Col>
                      </Row>
                    </h2>
                    <div
                      className="accordion-collapse"
                      style={{ display: isCardVisible9 ? "block" : "none" }}
                    >
                      <div className="accordion-body">
                        <Row className="commTopButtonRightLeftPadding">
                          <Col xs={12} md={6} lg={4}>
                            <InputField
                              className="inputBox"
                              label="Supplier RFQ Status"
                              name="rfxSupplierHeaderStatus"
                              value={formInputs?.rfxSupplierHeaderStatus || ""}
                              disabled={true}
                            />
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>
              )}

                    </div>
                    <div
                      className={`tab-pane fade ${
                        activeTab === "quote-summary" ? "show active" : ""
                      }`}
                      id="quote-summary"
                      role="tabpanel"
                      aria-labelledby="quote-summary-tab"
                    >
              {/* 10 - Quote Summary */}
              <div className="accordion mb-4" id="rfx-accordion-10">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility10}
                          className={`bi ${
                            isCardVisible10
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">QS</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">Quote Summary</span>
                        <span className="subHeaderTitle">Quote Totals</span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible10 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row className="commTopButtonRightLeftPadding">
                        <Col xs={12} md={6} lg={4}>
                          <InputField
                            className="inputBox"
                            label="Sub Total (Line Items)"
                            name="qtnSubTotalLineItem"
                            value={
                              formInputs?.qtnSubTotalLineItem != null
                                ? Number(
                                    formInputs?.qtnSubTotalLineItem,
                                  ).toLocaleString()
                                : ""
                            }
                            disabled={true}
                          />
                        </Col>
                        <Col xs={12} md={6} lg={4}>
                          <InputField
                            className="inputBox"
                            label="Sub Total (Other Charges)"
                            name="qtnSubTotalOtherCharges"
                            value={
                              formInputs?.qtnSubTotalOtherCharges != null
                                ? Number(
                                    formInputs?.qtnSubTotalOtherCharges,
                                  ).toLocaleString()
                                : ""
                            }
                            disabled={true}
                          />
                        </Col>
                        <Col xs={12} md={6} lg={4}>
                          <InputField
                            className="inputBox"
                            label="Total"
                            name="qtnTotal"
                            value={
                              formInputs?.qtnTotal != null
                                ? Number(formInputs?.qtnTotal).toLocaleString()
                                : ""
                            }
                            disabled={true}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

                    </div>
                    <div
                      className={`tab-pane fade ${
                        activeTab === "tech-comm" ? "show active" : ""
                      }`}
                      id="tech-comm"
                      role="tabpanel"
                      aria-labelledby="tech-comm-tab"
                    >
              {/* 11 - Commercial Questionnaire */}
              <div className="accordion mb-4" id="rfx-accordion-11">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility11}
                          className={`bi ${
                            isCardVisible11
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">CQ</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">
                          Commercial Questionnaire 
                        </span>
                        <span className="subHeaderTitle">
                          Total Weightage: {commercialWeightSum} /{" "}
                          {formInputs.commercialTotalWeigtage || 0}
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible11 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row className="commTopButtonRightLeftPadding">
                        {Array.isArray(formInputs?.formCOMM) &&
                        formInputs.formCOMM.length > 0 ? (
                          formInputs.formCOMM.map((formJsonData, index) => (
                            <RenderInputField
                              questionData={formJsonData}
                              onViewAttachment={handleViewAttachment}
                              key={index}
                            />
                          ))
                        ) : (
                          <Col xs={12}>
                            <p className="text-muted">
                              No commercial questions configured.
                            </p>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

              {/* 12 - Technical Questionnaire */}
              <div className="accordion mb-4" id="rfx-accordion-12">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility12}
                          className={`bi ${
                            isCardVisible12
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">TQ</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">
                          Technical Questionnaire
                        </span>
                        <span className="subHeaderTitle">
                          Total Weightage: {technicalWeightSum} /{" "}
                          {formInputs.technicalTotalWeight || 0} (Min:{" "}
                          {formInputs.minimumTechnicalTotalWeight || 0})
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible12 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row className="commTopButtonRightLeftPadding">
                        {Array.isArray(formInputs?.formTECH) &&
                        formInputs.formTECH.length > 0 ? (
                          formInputs.formTECH.map((formJsonData, index) => (
                            <RenderInputField
                              questionData={formJsonData}
                              onViewAttachment={handleViewAttachment}
                              key={index}
                            />
                          ))
                        ) : (
                          <Col xs={12}>
                            <p className="text-muted">
                              No technical questions configured.
                            </p>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

                    </div>
                    <div
                      className={`tab-pane fade ${
                        activeTab === "schedule" ? "show active" : ""
                      }`}
                      id="schedule"
                      role="tabpanel"
                      aria-labelledby="schedule-tab"
                    >
              {/* Schedule - Opening / Closing date & time (moved from the
                  Header Info accordion, same as the Create RFQ Schedule tab) */}
              <div className="accordion mb-4" id="rfx-accordion-schedule">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row style={{ padding: "6px 14px", alignItems: "center" }}>
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          onClick={toggleCardVisibility3}
                          className={`bi ${
                            isCardVisible3
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">HI</span>
                      </Col>
                      <Col xs={8} md={9} lg={9}>
                        <span className="headerTitle">Header Info</span>
                        <span className="subHeaderTitle">
                          Header Information
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className="accordion-collapse"
                    style={{ display: isCardVisible3 ? "block" : "none" }}
                  >
                    <div className="accordion-body">
                      <Row>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Opening Date Time"
                            name="openingDateTime"
                            value={
                              formatDateTime(formInputs?.openingDateTime) || ""
                            }
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Closing Date Time"
                            name="closingDateTime"
                            value={
                              formatDateTime(formInputs?.closingDateTime) || ""
                            }
                            disabled={true}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

                    </div>
                  </div>
                </div>
              </div>

              <div className="pageFooter mb-4 pb-2">
                <Row className="align-items-center">
                  <Col xs={12} md={3} lg={3} className="mb-2">
                    {currentTabIndex > 0 && (
                      <button
                        type="button"
                        className="btnTable"
                        onClick={goToPreviousTab}
                      >
                        <i className="bi bi-arrow-left me-2"></i> Previous
                      </button>
                    )}
                  </Col>
                  <Col xs={12} md={3} lg={3} className="mb-2 ms-auto text-end">
                    {!isLastTab && (
                      <button
                        type="button"
                        className="btnTable"
                        onClick={goToNextTab}
                      >
                        Next <i className="bi bi-arrow-right ms-2"></i>
                      </button>
                    )}
                  </Col>
                </Row>
              </div>

            </div>
          </Col>
        )}
      </Row>

      <FileViewerModal
        show={getShowFileViewModal}
        onHide={handleViewFileHide}
        fileid={getFileViewData}
      />
    </main>
  );
};

export default RfxEvaluationDetails;